import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BsBuilding } from "react-icons/bs";
import { LuUsers } from "react-icons/lu";
import { IoAddSharp } from "react-icons/io5";
import Header from "../component/Header";
import { FiDollarSign, FiTrash2, FiSearch, FiX, FiChevronDown, FiEdit2 } from "react-icons/fi";
import { API_BASE_URL } from "../utils/apiConfig";
import { FaRupeeSign } from "react-icons/fa";
import Swal from 'sweetalert2';
function UserPage() {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState("");
    const [todayRevenue, setTodayRevenue] = useState(0);
    const [loading, setLoading] = useState(true);
    const [expandedEmails, setExpandedEmails] = useState({});
    const [searchQuery, setSearchQuery] = useState("");
    const [deviceSearchQuery, setDeviceSearchQuery] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);
    const [editGateway, setEditGateway] = useState("razorpay");
    const [isUpdatingGateway, setIsUpdatingGateway] = useState(false);
    const dropdownRef = useRef(null);
    const userRole = localStorage.getItem("role");

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const toggleEmailExpand = (email) => {
        setExpandedEmails(prev => ({
            ...prev,
            [email]: !prev[email]
        }));
    };
    const currentUserId = localStorage.getItem("user_id_PK");

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) {
                    navigate("/");
                    return;
                }
                const config = { headers: { Authorization: `Bearer ${token}` } };

                const usersRes = await axios.get(`${API_BASE_URL}/api/admins`, config);
                setUsers(usersRes.data);

                const devicesRes = await axios.get(`${API_BASE_URL}/api/devices`, config);
                setDevices(devicesRes.data);
            } catch (err) {
                console.error("Failed to fetch data", err);
            } finally {
                setLoading(false);
            }
        };
        fetchInitialData();
    }, [navigate]);

    useEffect(() => {
        const fetchRevenue = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) return;
                const config = { headers: { Authorization: `Bearer ${token}` } };

                let url = `${API_BASE_URL}/api/orders/today-revenue`;
                if (selectedDeviceId) {
                    url += `?device_id=${selectedDeviceId}`;
                }
                const revenueRes = await axios.get(url, config);
                if (revenueRes.data.status) {
                    setTodayRevenue(revenueRes.data.todayRevenue);
                }
            } catch (err) {
                console.error("Failed to fetch revenue", err);
            }
        };
        fetchRevenue();
    }, [selectedDeviceId]);

    const deleteUser = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You want to remove this user? This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#22c55e",
            cancelButtonColor: "#ef4444",
            confirmButtonText: "Yes, delete user!",
            background: '#1b1d1a',
            color: '#fff'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = localStorage.getItem("token");
                    await axios.delete(`${API_BASE_URL}/api/admins/${id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setUsers(users.filter(u => u.user_id_PK !== id));
                    Swal.fire({ icon: 'success', title: 'Deleted!', text: 'User has been removed.', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
                } catch (err) {
                    Swal.fire({ icon: 'error', title: 'Error', text: "Failed to delete user", background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
                }
            }
        });
    };

    const handleOpenEditModal = (user, e) => {
        if (e) e.stopPropagation();
        setSelectedUserForEdit(user);
        setEditGateway(user.payment_gateway || user.paymentGateway || "razorpay");
    };

    const handleSaveGateway = async () => {
        if (!selectedUserForEdit) return;
        setIsUpdatingGateway(true);
        try {
            const token = localStorage.getItem("token");
            const res = await axios.put(`${API_BASE_URL}/api/admins/${selectedUserForEdit.user_id_PK}`, {
                payment_gateway: editGateway
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.status) {
                setUsers(prev => prev.map(u =>
                    u.user_id_PK === selectedUserForEdit.user_id_PK
                        ? { ...u, payment_gateway: editGateway, paymentGateway: editGateway }
                        : u
                ));
                Swal.fire({
                    icon: 'success',
                    title: 'Gateway Updated!',
                    text: `Payment Gateway successfully set to ${editGateway.toUpperCase()}`,
                    background: '#1b1d1a',
                    color: '#fff',
                    confirmButtonColor: '#22c55e'
                });
                setSelectedUserForEdit(null);
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Update Failed',
                    text: res.data.message || 'Could not update gateway',
                    background: '#1b1d1a',
                    color: '#fff',
                    confirmButtonColor: '#ef4444'
                });
            }
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: err.response?.data?.message || 'Server error occurred while updating gateway',
                background: '#1b1d1a',
                color: '#fff',
                confirmButtonColor: '#ef4444'
            });
        } finally {
            setIsUpdatingGateway(false);
        }
    };

    const currentUser = users.find(u => String(u.user_id_PK) === String(currentUserId));
    const currentUserEmail = currentUser ? currentUser.email : null;

    const filteredUsers = userRole === 'superadmin'
        ? users
        : users.filter(u => u.email === currentUserEmail);

    let displayedUsers = selectedDeviceId
        ? filteredUsers.filter(u => String(u.device_id) === String(selectedDeviceId))
        : filteredUsers;

    if (searchQuery) {
        const query = searchQuery.toLowerCase();
        displayedUsers = displayedUsers.filter(u =>
            (u.name?.toLowerCase().includes(query)) ||
            (u.email?.toLowerCase().includes(query)) ||
            (u.organization_name?.toLowerCase().includes(query)) ||
            (u.device_name?.toLowerCase().includes(query)) ||
            (u.location?.toLowerCase().includes(query))
        );
    }

    const groupedUsers = Object.values(displayedUsers.reduce((acc, user) => {
        if (!acc[user.email]) {
            acc[user.email] = [];
        }
        acc[user.email].push(user);
        return acc;
    }, {}));

    const adminCount = displayedUsers.filter(u => u.role === 'admin').length;
    const techCount = displayedUsers.filter(u => u.role === 'technician').length;

    if (loading) return <div className="bg-black min-h-screen text-white flex items-center justify-center">Loading User Accounts...</div>;

    return (
        <div className="bg-black min-h-screen text-white p-6">
            <Header />

            <div className="mt-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h2 className="text-xl font-bold text-white">Dashboard Overview</h2>
                    <p className="text-gray-500 text-xs mt-1">Key metrics across operations</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8 ">
                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition">
                    <div className="flex items-start gap-3">
                        <BsBuilding className="text-green-400 text-3xl mt-3" />
                        <div>
                            <p className="text-gray-400 text-sm">Total Device Partners</p>
                            <h2 className="text-green-400 text-3xl font-bold mt-1">{adminCount}</h2>
                        </div>
                    </div>
                </div>

                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition">
                    <div className="flex items-start gap-3">
                        <LuUsers className="text-blue-400 text-3xl mt-3" />
                        <div>
                            <p className="text-gray-400 text-sm">Active Technicians</p>
                            <h2 className="text-blue-400 text-3xl font-bold mt-1">{techCount}</h2>
                        </div>
                    </div>
                </div>

                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition">
                    <div className="flex items-start gap-3">
                        <FaRupeeSign className="text-green-400 text-3xl mt-3" />
                        <div>
                            <p className="text-gray-400 text-sm">Today's Revenue</p>
                            <h2 className="text-green-400 text-3xl font-bold mt-1">₹{todayRevenue.toLocaleString()}</h2>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6 mt-8">
                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg">
                    <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                        <h2 className="text-xl font-bold text-green-400">User List</h2>

                        <div className="flex items-center gap-3 flex-wrap w-full sm:w-auto">
                            {/* Device Selector */}
                            <div className="relative h-9 w-32 md:w-40 shrink-0" ref={dropdownRef}>
                                <div
                                    className="h-full bg-black/40 border border-white/5 text-gray-300 rounded-xl pl-3 pr-2 cursor-pointer flex justify-between items-center transition hover:border-green-500/50"
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                >
                                    <span className="truncate pr-2 text-[11px] font-bold">
                                        {selectedDeviceId ? (() => {
                                            const dev = devices.find(d => String(d.device_id_PK) === String(selectedDeviceId));
                                            return dev ? `${dev.device_name}${dev.user_location ? ` (${dev.user_location})` : (dev.location ? ` (${dev.location})` : '')}` : "All Devices";
                                        })() : "All Devices"}
                                    </span>
                                    <FiChevronDown size={11} className={`transition-transform text-gray-600 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                                </div>

                                {isDropdownOpen && (
                                    <div className="absolute top-[calc(100%+8px)] left-0 z-50 w-64 bg-[#111211] border border-gray-800 rounded-xl shadow-2xl overflow-hidden">
                                        <div className="p-3 border-b border-gray-800">
                                            <div className="relative">
                                                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={12} />
                                                <input
                                                    type="text"
                                                    placeholder="Search Machine..."
                                                    value={deviceSearchQuery}
                                                    onChange={(e) => setDeviceSearchQuery(e.target.value)}
                                                    className="w-full bg-black border border-gray-800 text-gray-200 rounded-lg pl-8 pr-3 py-2 text-[11px] font-bold focus:outline-none focus:border-green-500/50 transition-colors placeholder:text-gray-600"
                                                    autoFocus
                                                />
                                            </div>
                                        </div>
                                        <div className="max-h-64 overflow-y-auto custom-scrollbar text-[11px] font-bold p-1">
                                            <div
                                                className={`px-3 py-2 mx-1 mb-1 rounded-lg cursor-pointer transition-colors truncate ${!selectedDeviceId ? 'bg-green-500/10 text-green-400' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
                                                onClick={() => {
                                                    setSelectedDeviceId("");
                                                    setIsDropdownOpen(false);
                                                    setDeviceSearchQuery("");
                                                }}
                                            >
                                                All Devices
                                            </div>
                                            {(() => {
                                                const filtered = devices.filter(d =>
                                                    (d.device_name || "").toLowerCase().includes(deviceSearchQuery.toLowerCase()) ||
                                                    (d.user_location || "").toLowerCase().includes(deviceSearchQuery.toLowerCase()) ||
                                                    (d.location || "").toLowerCase().includes(deviceSearchQuery.toLowerCase())
                                                );
                                                if (filtered.length === 0) return <div className="px-3 py-4 text-gray-500 text-center font-normal">No machines found</div>;
                                                return filtered.map(device => (
                                                    <div
                                                        key={device.device_id_PK}
                                                        className={`px-3 py-2 mx-1 mb-1 rounded-lg cursor-pointer transition-colors truncate ${String(selectedDeviceId) === String(device.device_id_PK) ? 'bg-green-500/10 text-green-400' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
                                                        onClick={() => {
                                                            setSelectedDeviceId(device.device_id_PK);
                                                            setIsDropdownOpen(false);
                                                            setDeviceSearchQuery("");
                                                        }}
                                                        title={`${device.device_name}${device.user_location ? ` (${device.user_location})` : (device.location ? ` (${device.location})` : '')}`}
                                                    >
                                                        {device.device_name} {(device.user_location || device.location) && <span className="text-gray-400 text-xs">({device.user_location || device.location})</span>}
                                                    </div>
                                                ));
                                            })()}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Search bar */}
                            <div className="relative flex items-center h-9">
                                <FiSearch size={13} className="absolute left-3 text-gray-500" />
                                <input
                                    type="text"
                                    placeholder="Search users..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="h-full bg-black/40 border border-white/5 text-gray-300 text-[11px] font-bold rounded-xl pl-8 pr-8 focus:outline-none focus:ring-1 focus:ring-green-500 transition w-48 md:w-64 placeholder:text-gray-600"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery("")}
                                        className="absolute right-3 text-gray-500 hover:text-red-400 transition"
                                    >
                                        <FiX size={13} />
                                    </button>
                                )}
                            </div>

                            {userRole === 'superadmin' && (
                                <button
                                    onClick={() => navigate("/addUser")}
                                    className="flex items-center gap-2 px-4 h-9 text-sm bg-green-400 font-semibold rounded-xl text-black hover:bg-green-500 transition shadow-lg shadow-green-900/20 active:scale-95 shrink-0"
                                >
                                    <IoAddSharp size={16} /> Add New Account
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm text-gray-300">
                            <thead>
                                <tr className="border-b border-gray-700 text-gray-500 uppercase tracking-wider text-[11px]">
                                    <th className="px-4 py-3 text-left">Organization</th>
                                    <th className="px-4 py-3 text-left">Device Name</th>
                                    <th className="px-4 py-3 text-left">User Name</th>
                                    <th className="px-4 py-3 text-left">Email</th>
                                    <th className="px-4 py-3 text-left">Location</th>
                                    <th className="px-4 py-3 text-left">Role</th>
                                    <th className="px-4 py-3 text-left">Gateway</th>
                                    <th className="px-4 py-3 text-left">Linked Device ID</th>
                                    <th className="px-4 py-3 text-right">Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {groupedUsers.map((group) => {
                                    const primaryUser = group[0];
                                    const isMultiple = group.length > 1;
                                    const isExpanded = expandedEmails[primaryUser.email];

                                    return (
                                        <React.Fragment key={primaryUser.email}>
                                            <tr
                                                className={`border-b border-gray-800 hover:bg-[#1e1e1e] transition ${isMultiple ? 'cursor-pointer' : ''}`}
                                                onClick={() => isMultiple && toggleEmailExpand(primaryUser.email)}
                                            >
                                                <td className="px-4 py-3">
                                                    <p className="text-white font-semibold">{primaryUser.organization_name}</p>
                                                </td>
                                                <td className="px-4 py-3 text-gray-400">
                                                    {isMultiple ? (
                                                        <span className="text-green-400 font-semibold">{group.length} Devices (Click to {isExpanded ? 'Collapse' : 'Expand'})</span>
                                                    ) : (
                                                        primaryUser.device_name
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-gray-400">{primaryUser.name}</td>
                                                <td className="px-4 py-3 text-gray-400">{primaryUser.email}</td>
                                                <td className="px-4 py-3 text-gray-400">{primaryUser.location}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${primaryUser.role === 'admin' ? 'border-green-400 text-green-400' : 'border-blue-400 text-blue-400'}`}>
                                                        {primaryUser.role}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); if (userRole === 'superadmin') handleOpenEditModal(primaryUser, e); }}
                                                        className={`group flex items-center gap-1.5 text-left ${userRole === 'superadmin' ? 'cursor-pointer' : 'cursor-default'}`}
                                                        title={userRole === 'superadmin' ? "Click to change payment gateway" : ""}
                                                    >
                                                        {primaryUser.paymentGateway || primaryUser.payment_gateway ? (
                                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border transition ${(primaryUser.paymentGateway || primaryUser.payment_gateway) === 'paytm' ? 'bg-blue-900/20 text-blue-400 border-blue-400/30 group-hover:border-blue-400' : 'bg-gray-800/50 text-gray-300 border-gray-600 group-hover:border-gray-400'}`}>
                                                                {primaryUser.paymentGateway || primaryUser.payment_gateway}
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-500 text-xs italic group-hover:text-gray-400">Not Set</span>
                                                        )}
                                                        {userRole === 'superadmin' && (
                                                            <FiEdit2 size={11} className="text-gray-600 group-hover:text-green-400 transition" />
                                                        )}
                                                    </button>
                                                </td>
                                                <td className="px-4 py-3">
                                                    {isMultiple ? (
                                                        <span className="px-3 py-1 text-[11px] font-semibold rounded-full bg-gray-900 border border-gray-800 text-green-400">
                                                            {isExpanded ? '▼ Collapse' : '▶ Expand'}
                                                        </span>
                                                    ) : (
                                                        <span className="px-3 py-1 text-[11px] font-semibold rounded-full bg-gray-900 border border-gray-800 text-gray-400">
                                                            ID: {primaryUser.device_id || 'unassigned'}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        {userRole === 'superadmin' && (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => handleOpenEditModal(primaryUser, e)}
                                                                title="Update Payment Gateway"
                                                                className="px-2.5 py-1 flex items-center gap-1.5 text-xs font-semibold text-green-400 bg-green-500/10 hover:bg-green-500/20 border border-green-500/30 rounded-lg transition active:scale-95"
                                                            >
                                                                <FiEdit2 size={12} />
                                                                {/* <span>Update</span> */}
                                                            </button>
                                                        )}
                                                        {!isMultiple && userRole === 'superadmin' && (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); deleteUser(primaryUser.user_id_PK); }}
                                                                className="p-1.5 text-gray-500 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition"
                                                                title="Delete User"
                                                            >
                                                                <FiTrash2 size={14} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                            {isMultiple && isExpanded && group.map(user => (
                                                <tr key={user.user_id_PK} className="border-b border-gray-800 bg-[#161815] hover:bg-[#1e1e1e] transition">
                                                    <td className="px-4 py-3 pl-8 border-l-2 border-green-500">
                                                        <p className="text-gray-400 text-xs">↳ {user.organization_name}</p>
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-400">{user.device_name}</td>
                                                    <td className="px-4 py-3 text-gray-500">{user.name}</td>
                                                    <td className="px-4 py-3 text-gray-500">{user.email}</td>
                                                    <td className="px-4 py-3 text-gray-500">{user.location}</td>
                                                    <td className="px-4 py-3">
                                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${user.role === 'admin' ? 'border-green-400 text-green-400' : 'border-blue-400 text-blue-400'}`}>
                                                            {user.role}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.stopPropagation(); if (userRole === 'superadmin') handleOpenEditModal(user, e); }}
                                                            className={`group flex items-center gap-1.5 text-left ${userRole === 'superadmin' ? 'cursor-pointer' : 'cursor-default'}`}
                                                            title={userRole === 'superadmin' ? "Click to change payment gateway" : ""}
                                                        >
                                                            {user.paymentGateway || user.payment_gateway ? (
                                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border transition ${(user.paymentGateway || user.payment_gateway) === 'paytm' ? 'bg-blue-900/20 text-blue-400 border-blue-400/30 group-hover:border-blue-400' : 'bg-gray-800/50 text-gray-300 border-gray-600 group-hover:border-gray-400'}`}>
                                                                    {user.paymentGateway || user.payment_gateway}
                                                                </span>
                                                            ) : (
                                                                <span className="text-gray-500 text-xs italic group-hover:text-gray-400">Not Set</span>
                                                            )}
                                                            {userRole === 'superadmin' && (
                                                                <FiEdit2 size={11} className="text-gray-600 group-hover:text-green-400 transition" />
                                                            )}
                                                        </button>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className="px-3 py-1 text-[11px] font-semibold rounded-full bg-gray-900 border border-gray-800 text-gray-400">
                                                            ID: {user.device_id || 'unassigned'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        <div className="flex items-center justify-end gap-2">
                                                            {userRole === 'superadmin' && (
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => handleOpenEditModal(user, e)}
                                                                    title="Update Payment Gateway"
                                                                    className="px-2.5 py-1 flex items-center gap-1.5 text-xs font-semibold text-green-400 bg-green-500/10 hover:bg-green-500/20 border border-green-500/30 rounded-lg transition active:scale-95"
                                                                >
                                                                    <FiEdit2 size={12} />
                                                                    <span>Update</span>
                                                                </button>
                                                            )}
                                                            {userRole === 'superadmin' && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => deleteUser(user.user_id_PK)}
                                                                    className="p-1.5 text-gray-500 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition"
                                                                    title="Delete User"
                                                                >
                                                                    <FiTrash2 size={14} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Edit Payment Gateway Modal */}
            {selectedUserForEdit && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                    <div className="bg-[#1b1d1a] border border-gray-800 rounded-3xl p-6 sm:p-8 w-full max-w-md shadow-2xl relative">
                        {/* Close Button */}
                        <button
                            type="button"
                            onClick={() => setSelectedUserForEdit(null)}
                            className="absolute top-5 right-5 text-gray-500 hover:text-white p-1 rounded-lg transition"
                        >
                            <FiX size={18} />
                        </button>

                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-2xl text-green-400">
                                <FiDollarSign size={22} />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-white">Update Payment Gateway</h3>
                                <p className="text-xs text-gray-400 mt-0.5">Change active gateway for this machine user</p>
                            </div>
                        </div>

                        {/* User Info Details Box */}
                        <div className="bg-black/50 border border-gray-800/80 rounded-2xl p-4 mb-6 space-y-2 text-xs">
                            <div className="flex justify-between">
                                <span className="text-gray-500">User Name:</span>
                                <span className="text-gray-200 font-medium">{selectedUserForEdit.name}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Email:</span>
                                <span className="text-gray-200 font-medium">{selectedUserForEdit.email}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Machine / Device:</span>
                                <span className="text-green-400 font-semibold">{selectedUserForEdit.device_name || `ID: ${selectedUserForEdit.device_id}`}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Location:</span>
                                <span className="text-gray-300">{selectedUserForEdit.location || 'N/A'}</span>
                            </div>
                        </div>

                        {/* Gateway Selection Cards */}
                        <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                            Select Payment Gateway
                        </label>
                        <div className="grid grid-cols-2 gap-3 mb-6">
                            {/* Razorpay Option */}
                            <div
                                onClick={() => setEditGateway("razorpay")}
                                className={`cursor-pointer border rounded-2xl p-4 transition-all flex flex-col justify-between ${editGateway === "razorpay"
                                    ? "bg-emerald-500/10 border-emerald-500 text-white shadow-lg shadow-emerald-950/30"
                                    : "bg-black/30 border-gray-800 text-gray-400 hover:border-gray-700 hover:text-gray-200"
                                    }`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-bold text-sm">Razorpay</span>
                                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${editGateway === "razorpay" ? "border-emerald-500 bg-emerald-500" : "border-gray-700"
                                        }`}>
                                        {editGateway === "razorpay" && <div className="w-1.5 h-1.5 rounded-full bg-black" />}
                                    </div>
                                </div>
                                <p className="text-[10px] text-gray-500 leading-tight">Razorpay Dynamic UPI QR & Cards</p>
                            </div>

                            {/* Paytm Option */}
                            <div
                                onClick={() => setEditGateway("paytm")}
                                className={`cursor-pointer border rounded-2xl p-4 transition-all flex flex-col justify-between ${editGateway === "paytm"
                                    ? "bg-blue-500/10 border-blue-500 text-white shadow-lg shadow-blue-950/30"
                                    : "bg-black/30 border-gray-800 text-gray-400 hover:border-gray-700 hover:text-gray-200"
                                    }`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-bold text-sm">Paytm</span>
                                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${editGateway === "paytm" ? "border-blue-500 bg-blue-500" : "border-gray-700"
                                        }`}>
                                        {editGateway === "paytm" && <div className="w-1.5 h-1.5 rounded-full bg-black" />}
                                    </div>
                                </div>
                                <p className="text-[10px] text-gray-500 leading-tight">Paytm UPI QR & Direct Business</p>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => setSelectedUserForEdit(null)}
                                disabled={isUpdatingGateway}
                                className="w-1/2 py-2.5 px-4 bg-gray-900 hover:bg-gray-800 text-gray-300 font-semibold text-xs rounded-xl border border-gray-800 transition disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveGateway}
                                disabled={isUpdatingGateway}
                                className="w-1/2 py-2.5 px-4 bg-green-500 hover:bg-green-600 text-black font-bold text-xs rounded-xl transition shadow-lg shadow-green-950/50 flex items-center justify-center gap-2 disabled:opacity-50 active:scale-95"
                            >
                                {isUpdatingGateway ? (
                                    <>
                                        <div className="w-3.5 h-3.5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                                        <span>Updating...</span>
                                    </>
                                ) : (
                                    <span>Save Changes</span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default UserPage;
